<template>
  <div>
    <Navbar v-if="$route.name === 'event'" ref="navbar" />
    <!-- <Breadcrumb v-if="$route.name === 'event'" /> -->
    <div class="px-3">
      <div class="event-detail-header d-flex align-items-center mb-3" :style="{ top: navbarHeight + 'px' }">
        <button @click.prevent="goBack" class="btn btn-outline-primary">&lt; Return to list</button>
        <h3 class="mb-0 ms-4">{{ isNew ? 'New Event' : 'Edit Event' }}</h3>
        <div v-if="!isNew && !loading" class="ms-auto d-flex align-items-center flex-nowrap gap-2">
          <label class="col-form-label mb-0">Public link</label>
          <select class="form-select" style="width: 140px" v-model="linkLang">
            <option v-for="l in linkLangs" :key="l.language_code" :value="l.language_code">{{ l.language_name }}</option>
          </select>
          <code class="small" :title="publicLink" style="max-width: 340px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ publicLink }}</code>
          <button class="btn btn-outline-primary btn-sm" @click="copyLink">Copy</button>
          <a class="btn btn-outline-primary btn-sm" :href="publicLink" target="_blank" rel="noopener">Open</a>
        </div>
      </div>

      <div class="text-center py-5" v-if="loading">
        <div class="spinner-border text-primary"></div>
      </div>

      <div class="card border-0 mx-0 px-0" v-else>
        <div class="card-body">
          <div class="alert alert-danger" v-if="errorMsg">{{ errorMsg }}</div>
          <div class="alert alert-success" v-if="successMsg">{{ successMsg }}</div>

          <form autocomplete="off" @submit.stop.prevent>
            <div class="row">
              <div class="col-12 col-md-6">
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Slug</label>
                  <div style="width: 67%"><input class="form-control" v-model="event.slug" /></div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Category</label>
                  <div style="width: 67%">
                    <select class="form-select" v-model="event.category_id">
                      <option :value="0">None</option>
                      <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                    </select>
                  </div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Status</label>
                  <div style="width: 67%">
                    <select class="form-select" v-model="event.status">
                      <option value="draft">Draft</option>
                      <option value="public">Public</option>
                      <option value="past">Past</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="postponed">Postponed</option>
                    </select>
                  </div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Delivery language</label>
                  <div style="width: 67%">
                    <select class="form-select" v-model="event.event_language_id">
                      <option :value="0">None</option>
                      <option v-for="l in languages" :key="l.id" :value="l.id">{{ l.language_name }}</option>
                    </select>
                  </div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Start</label>
                  <div style="width: 67%"><Datetime type="datetime" format="yyyy-LL-dd'T'HH:mm" zone="Europe/Stockholm" value-zone="Europe/Stockholm" input-class="form-control col-9 gdq-w-150px" v-model="event.start_dt" /></div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">End</label>
                  <div style="width: 67%"><Datetime type="datetime" format="yyyy-LL-dd'T'HH:mm" zone="Europe/Stockholm" value-zone="Europe/Stockholm" input-class="form-control col-9 gdq-w-150px" v-model="event.end_dt" :disabled="event.all_day" /></div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" for="allday" style="width: 33%; flex-shrink: 0">All-day event</label>
                  <div style="width: 67%">
                    <div class="form-check">
                      <input type="checkbox" class="form-check-input" id="allday" v-model="event.all_day" />
                    </div>
                  </div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" for="virtual" style="width: 33%; flex-shrink: 0">Virtual / online</label>
                  <div style="width: 67%">
                    <div class="form-check">
                      <input type="checkbox" class="form-check-input" id="virtual" v-model="event.is_virtual" />
                    </div>
                  </div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Location name</label>
                  <div style="width: 67%"><input class="form-control" v-model="event.location_name" :disabled="event.is_virtual" /></div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">City</label>
                  <div style="width: 67%"><input class="form-control" v-model="event.location_city" :disabled="event.is_virtual" /></div>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Organizer name</label>
                  <div style="width: 67%"><input class="form-control" v-model="event.organizer_name" /></div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Organizer e-mail</label>
                  <div style="width: 67%"><input class="form-control" v-model="event.organizer_email" /></div>
                </div>
                <div class="mb-3 d-flex align-items-start">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Organizer photo (URL)</label>
                  <div class="d-flex align-items-start gap-2" style="width: 67%">
                    <input class="form-control" v-model="event.organizer_photo" />
                    <img v-if="event.organizer_photo" :src="event.organizer_photo" style="height: 64px; width: 64px; object-fit: cover; flex-shrink: 0; cursor: pointer" title="Click to enlarge" @click="showImage(event.organizer_photo)" alt="Organizer photo preview" />
                  </div>
                </div>
                <div class="mb-3 d-flex align-items-start">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Image (URL)</label>
                  <div class="d-flex align-items-start gap-2" style="width: 67%">
                    <input class="form-control" v-model="event.image" />
                    <img v-if="event.image" :src="event.image" style="height: 64px; width: 64px; object-fit: cover; flex-shrink: 0; cursor: pointer" title="Click to enlarge" @click="showImage(event.image)" alt="Event image preview" />
                  </div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Price</label>
                  <div style="width: 67%"><input type="number" step="0.01" class="form-control" v-model.number="event.price" /></div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Currency</label>
                  <div style="width: 67%"><input class="form-control" v-model="event.currency" /></div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Seat limit (0 = unlimited)</label>
                  <div style="width: 67%"><input type="number" class="form-control" v-model.number="event.seat_limit" /></div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Stop selling date</label>
                  <div style="width: 67%"><Datetime type="date" input-class="form-control col-9 gdq-w-150px" v-model="event.stop_selling_date" /></div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" style="width: 33%; flex-shrink: 0">Start selling date</label>
                  <div style="width: 67%"><Datetime type="date" input-class="form-control col-9 gdq-w-150px" v-model="event.start_selling_date" /></div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" for="autoverify" style="width: 33%; flex-shrink: 0">Auto-verify bookings</label>
                  <div style="width: 67%">
                    <div class="form-check">
                      <input type="checkbox" class="form-check-input" id="autoverify" v-model="event.auto_verify" />
                    </div>
                  </div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" for="autoconfirm" style="width: 33%; flex-shrink: 0">Auto-confirm bookings</label>
                  <div style="width: 67%">
                    <div class="form-check">
                      <input type="checkbox" class="form-check-input" id="autoconfirm" v-model="event.auto_confirm" />
                    </div>
                  </div>
                </div>
                <div class="mb-3 d-flex align-items-center">
                  <label class="col-form-label" for="regclosed" style="width: 33%; flex-shrink: 0">Registration closed (manual)</label>
                  <div style="width: 67%">
                    <div class="form-check">
                      <input type="checkbox" class="form-check-input" id="regclosed" v-model="event.registration_closed" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <div class="card">
                <div class="card-header text-start fw-bold">Languages</div>
                <div class="card-body">
                   <div class="d-flex justify-content-end mb-2 align-items-center">
                     <select class="form-select form-select-sm me-2" v-model="nextLang" :class="{ 'form-select-disabled': unusedLangs.length === 0 }">
                       <option value="" disabled selected>Choose language</option>
                       <option v-for="l in unusedLangs" :key="l.id" :value="l.language_code">{{ l.language_name }} ({{ l.language_code }})</option>
                     </select>
                     <button class="btn btn-primary" @click="addEventLang"><fa-icon name="plus" /> Add</button>
                   </div>
                  <table class="table table-bordered align-middle mb-0">
                    <thead>
                      <tr>
                        <th style="width: 18%;">Language</th>
                        <th>Title</th>
                        <th>Short description</th>
                        <th>Description</th>
                        <th style="width: 48px;"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(lr, li) in eventLangs" :key="li">
                        <td><strong>{{ langNames[lr.language_code] || lr.language_code }}</strong></td>
                        <td><input class="form-control form-control-sm" v-model="lr.title" @input="autoSlug" :placeholder="enPlaceholder(lr, 'title')" /></td>
                        <td><input class="form-control form-control-sm" v-model="lr.short_description" :placeholder="enPlaceholder(lr, 'short_description')" /></td>
                        <td><input class="form-control form-control-sm" v-model="lr.description" :placeholder="enPlaceholder(lr, 'description')" /></td>
                        <td class="text-end">
                          <fa-icon name="trash-alt" style="cursor: pointer" @click.prevent.stop="eventLangs.splice(li, 1)" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <div class="card">
                <div class="card-header text-start fw-bold">Attendee questions</div>
                <div class="card-body">
                  <div class="d-flex justify-content-end mb-2">
                    <button class="btn btn-primary" @click="addFormField"><fa-icon name="plus" /> Add field</button>
                  </div>
                  <div class="text-muted small mb-2">The label is edited per field in the selected language. These fields are collected per attendee on the booking page.</div>
                  <div class="row g-2 align-items-center mb-2" v-for="(f, fi) in formFields" :key="fi">
                  <div class="col-6 col-md-2">
                    <select class="form-select form-select-sm" v-model="f.type" @change="onFieldType(f)">
                      <option value="text">Text</option>
                      <option value="textarea">Text area</option>
                      <option value="mec_email">E-mail</option>
                      <option value="name">Full name</option>
                      <option value="radio">Radio</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="agreement">Agreement</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-6">
                    <input class="form-control form-control-sm" :placeholder="fieldPlaceholder(f)" :value="labelOf(f)" @input="setLabel(f, $event.target.value)" />
                  </div>
                  <div class="col-6 col-md-2">
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" :id="'mand' + fi" v-model="f.mandatory" :true-value="1" :false-value="0" />
                      <label class="form-check-label" :for="'mand' + fi">Required</label>
                    </div>
                  </div>
                  <div class="col-6 col-md-2 text-end">
                    <fa-icon name="trash-alt" style="cursor: pointer" @click.prevent.stop="removeFormField(fi)" />
                  </div>
                  <div class="col-12" v-if="f.type === 'radio' || f.type === 'checkbox'">
                    <div class="ms-4">
                      <div class="d-flex align-items-center mb-1" v-for="(o, oi) in f.options" :key="oi">
                        <input class="form-control form-control-sm me-2" v-model="f.options[oi].label" />
                        <button class="btn btn-sm btn-outline-danger" @click="f.options.splice(oi, 1)"><fa-icon name="times" /></button>
                      </div>
                      <button class="btn btn-sm btn-outline-secondary" @click="f.options.push({ label: '' })">Add option</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button class="btn btn-primary float-end" :disabled="saving" @click="save">
              <fa-icon name="check" />{{ saving ? 'Saving…' : 'Save event' }}
            </button>
          </form>
        </div>
      </div>
    </div>
    <!-- Image preview modal -->
    <div class="modal fade" :class="{ show: imageModal, 'd-block': imageModal }" tabindex="-1" :style="{ display: imageModal ? 'block' : 'none' }">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Image preview</h5>
            <button type="button" class="btn-close" @click="imageModal = false"></button>
          </div>
          <div class="modal-body">
            <img :src="imageModalSrc" class="img-fluid d-block" alt="Image preview" />
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="imageModal = false">Close</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="imageModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import Navbar from './Navbar.vue'
// import Breadcrumb from './Breadcrumb.vue'
import Datetime from './CustomControls/Datetime.vue'
import { config } from '../../config'

const emptyEvent = () => ({
  slug: '',
  status: 'draft',
  category_id: 0,
  start_dt: '',
  end_dt: '',
  all_day: false,
  location_name: '',
  location_city: '',
  is_virtual: false,
  event_language_id: 0,
  organizer_name: '',
  organizer_email: '',
  organizer_photo: '',
  image: '',
  price: 0,
  currency: 'SEK',
    seat_limit: 0,
    stop_selling_date: '',
    start_selling_date: '',
    registration_closed: false,
    auto_verify: false,
   auto_confirm: false
})

export default {
  name: 'Event',
  components: { Navbar, Datetime },
  data () {
    return {
      id: 0,
      event: emptyEvent(),
      formFields: [],
      languages: [],
      categories: [],
      eventLangs: [{ language_code: 'en', title: '', short_description: '', description: '' }],
      loading: true,
      saving: false,
      imageModal: false,
      imageModalSrc: '',
        linkLang: '',
        nextLang: '',
        errorMsg: '',
        successMsg: '',
        navbarHeight: 0
     }
  },
  mounted () {
    this.$nextTick(() => {
      if (this.$refs.navbar && this.$refs.navbar.$el) {
        this.navbarHeight = this.$refs.navbar.$el.offsetHeight
      }
    })
  },
  computed: {
    isNew () {
      return this.id <= 0
    },
    langNames () {
      return Object.fromEntries(this.languages.map(l => [l.language_code, l.language_name]))
    },
    linkLangs () {
      const rows = this.eventLangs.map(l => ({
        language_code: l.language_code,
        language_name: this.langNames[l.language_code] || l.language_code
      }))
      return rows.length ? rows : this.languages.map(l => ({ language_code: l.language_code, language_name: l.language_name }))
    },
    unusedLangs () {
      const used = this.eventLangs.map(l => l.language_code)
      return this.languages.filter(l => !used.includes(l.language_code))
    },
    publicLink () {
      return config.web_url + '/event/' + this.id + '?lang=' + (this.linkLang || 'en')
    }
  },
  created () {
    const rid = Number(this.$route.params.id)
    this.id = rid
    this.categories_get()
    this.languages_get()
    if (this.id > 0) {
      this.event_get().then(() => {
        this.loading = false
        const deliver = this.languages.find(l => l.id === this.event.event_language_id)
        const codes = this.linkLangs.map(l => l.language_code)
        this.linkLang = (deliver && codes.includes(deliver.language_code)) ? deliver.language_code : (codes[0] || 'en')
      })
    } else {
      this.loading = false
    }
  },
  methods: {
    showImage (src) {
      this.imageModalSrc = src
      this.imageModal = true
    },
    async categories_get () {
      try {
        const res = await this.$store.dispatch('event_categories', { lang: 'en' })
        this.categories = res.data.result
      } catch (e) { this.categories = [] }
    },
    async languages_get () {
      try {
        const res = await this.$store.dispatch('languages_fetch')
        this.languages = res.data.result
      } catch (e) { this.languages = [{ id: 38, language_code: 'en', language_name: 'English' }] }
    },
    async event_get () {
      const res = await this.$store.dispatch('event_get', { id: this.id, lang: 'en' })
      const r = res.data.result
      this.event = {
        slug: r.slug,
        status: r.status,
        category_id: r.category_id || 0,
        start_dt: this.dtLocal(r.start_dt),
        end_dt: this.dtLocal(r.end_dt),
        all_day: !!r.all_day,
        location_name: r.location_name || '',
        location_city: r.location_city || '',
        is_virtual: !!r.is_virtual,
        event_language_id: r.event_language_id || 0,
        organizer_name: r.organizer_name || '',
        organizer_email: r.organizer_email || '',
        organizer_photo: r.organizer_photo || '',
        image: r.image || '',
        price: Number(r.price) || 0,
        currency: r.currency || 'SEK',
        seat_limit: Number(r.seat_limit) || 0,
        stop_selling_date: r.stop_selling_date || '',
        start_selling_date: r.start_selling_date || '',
        registration_closed: !!r.registration_closed,
        auto_verify: !!r.auto_verify,
        auto_confirm: !!r.auto_confirm
      }
      this.eventLangs = (r.lang_rows || []).map(l => ({
        language_code: l.language_code,
        title: l.title || '',
        short_description: l.short_description || '',
        description: l.description || ''
      }))
      this.formFields = (r.form_fields && r.form_fields.fields) ? r.form_fields.fields : []
      this.formFields.forEach(f => {
        if (!f.label) f.label = {}
        if (!f.lang_code) f.lang_code = 'en'
      })
      return r
    },
    addEventLang () {
      const used = this.eventLangs.map(l => l.language_code)
      const candidates = this.languages.filter(l => !used.includes(l.language_code))
      const code = this.nextLang && candidates.some(l => l.language_code === this.nextLang) ? this.nextLang : (candidates[0] && candidates[0].language_code || null)
      if (!code) return
      this.eventLangs.push({ language_code: code, title: '', short_description: '', description: '' })
      this.nextLang = ''
    },
    labelOf (f) {
      const l = f.label || {}
      return l[this.linkLang] || ''
    },
    fieldPlaceholder (f) {
      const l = f.label || {}
      return l[this.linkLang] ? '' : (l.en || '')
    },
    setLabel (f, val) {
      if (!f.label) f.label = {}
      f.label[this.linkLang] = val
    },
    onFieldType (f) {
      if (f.type === 'radio' || f.type === 'checkbox') {
        if (!f.options) f.options = [{ label: '' }]
      } else {
        f.options = null
      }
      if (!f.label) f.label = {}
    },
    addFormField () {
      this.formFields.push({ type: 'text', lang_code: this.linkLang || 'en', label: {}, content: null, options: null, mandatory: 0 })
    },
    removeFormField (fi) {
      this.formFields.splice(fi, 1)
    },
    enRow () {
      return this.eventLangs.find(l => l.language_code === 'en') || null
    },
    enPlaceholder (lr, key) {
      if (lr.language_code === 'en' || lr[key]) return ''
      const en = this.enRow()
      return en ? en[key] : ''
    },
    autoSlug () {
      if (this.isNew) {
        const t = (this.eventLangs[0] && this.eventLangs[0].title) || ''
        this.event.slug = t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      }
    },
    dtLocal (s) {
      if (!s) return ''
      const m = String(s).match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/)
      return m ? m[1] + 'T' + m[2] : ''
    },
    dtApi (s) {
      if (!s) return null
      const m = String(s).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/)
      return m ? m[1] + ' ' + m[2] + ':00' : null
    },
    buildPayload () {
      const lang = {}
      this.eventLangs.forEach(lr => {
        if (lr.language_code) lang[lr.language_code] = { title: lr.title, short_description: lr.short_description, description: lr.description }
      })
      return {
        id: this.isNew ? 0 : this.id,
        slug: this.event.slug,
        status: this.event.status,
        category_id: this.event.category_id,
        start_dt: this.dtApi(this.event.start_dt),
        end_dt: this.event.all_day ? null : this.dtApi(this.event.end_dt),
        all_day: this.event.all_day ? 1 : 0,
        location_name: this.event.location_name,
        location_city: this.event.location_city,
        is_virtual: this.event.is_virtual ? 1 : 0,
        event_language_id: this.event.event_language_id,
        organizer_name: this.event.organizer_name,
        organizer_email: this.event.organizer_email,
        organizer_photo: this.event.organizer_photo,
        image: this.event.image,
        price: Number(this.event.price) || 0,
        currency: this.event.currency || 'SEK',
        seat_limit: Number(this.event.seat_limit) || 0,
        stop_selling_date: this.event.stop_selling_date || null,
        start_selling_date: this.event.start_selling_date || null,
        registration_closed: this.event.registration_closed ? 1 : 0,
        auto_verify: this.event.auto_verify ? 1 : 0,
        auto_confirm: this.event.auto_confirm ? 1 : 0,
        form_fields: { fields: this.formFields },
        lang
      }
    },
    async save () {
      this.errorMsg = ''
      this.successMsg = ''
      this.saving = true
      try {
        const payload = this.buildPayload()
        const res = await this.$store.dispatch('event_save', { body: payload })
        if (res.data.status !== 'ok') throw new Error(res.data.error || 'save failed')
        if (this.isNew) {
          this.id = res.data.id
          this.$router.replace('/event/' + this.id)
        }
        this.successMsg = 'Saved.'
        await this.event_get()
      } catch (e) {
        this.errorMsg = e.response && e.response.data && e.response.data.error
          ? e.response.data.error
          : (e.message || 'Save failed')
      }
      this.saving = false
    },
    async copyLink () {
      try {
        await navigator.clipboard.writeText(this.publicLink)
        this.successMsg = 'Link copied to clipboard'
        setTimeout(() => { this.successMsg = '' }, 2000)
      } catch (e) {
        this.errorMsg = 'Copy failed: ' + e.message
      }
    },
    goBack () {
      this.$router.push('/events')
    }
  }
}
</script>

<style scoped>
.event-detail-header {
  position: sticky;
  z-index: 100;
  background-color: #fff;
}
.event-detail-header h3 {
  line-height: 1;
}
</style>
